/* eslint-disable no-console */
// Diagnostic only, gated behind REACT_APP_DEBUG_VOICE_RECORDING. Records why each clip was cut
// and the level history behind it, so a wrong split can be read off the numbers rather than
// guessed at from the audio.

import { getVoiceTunable } from "./consts";

// Not `!!process.env...`: "0" and "false" are non-empty strings and would both read as ON, which
// is the one thing someone turning this off would try first.
const OFF_VALUES = ["", "0", "false"];
const enabled = !OFF_VALUES.includes(process.env.REACT_APP_DEBUG_VOICE_RECORDING ?? "");

export type VoiceCutReason = "hidden" | "maxClip" | "silence";

// Sized in frames, not milliseconds: a 120Hz display produces twice the samples a 60Hz one does,
// and losing the oldest history is better than overrunning the buffer.
const HISTORY_FRAMES = 512;
const WINDOW_MS = 1_500;
const BUCKET_MS = 50;

const levels = new Float32Array(HISTORY_FRAMES);
const durations = new Float32Array(HISTORY_FRAMES);
let writeIndex = 0;

export const debugRecordLevel = (rms: number, dtMs: number): void => {
  if (!enabled) {
    return;
  }
  levels[writeIndex % HISTORY_FRAMES] = rms;
  durations[writeIndex % HISTORY_FRAMES] = dtMs;
  writeIndex += 1;
};

export const debugResetLevels = (): void => {
  writeIndex = 0;
};

// Maximum per bucket, never the mean: a mean smears the peaks that decide isSpeech, which is the
// exact question being asked here.
const recentBuckets = (): number[] => {
  const buckets: number[] = [];
  let accMs = 0;
  let max = 0;
  for (let back = 0; back < HISTORY_FRAMES && back < writeIndex; back += 1) {
    const index = (writeIndex - 1 - back) % HISTORY_FRAMES;
    max = Math.max(max, levels[index]);
    accMs += durations[index];
    if (accMs >= BUCKET_MS) {
      buckets.push(max);
      max = 0;
      accMs -= BUCKET_MS;
      if (buckets.length * BUCKET_MS >= WINDOW_MS) {
        break;
      }
    }
  }
  return buckets.reverse();
};

// Five steps, not two: everything under stay used to print identically, so quiet speech at 0.008
// and a genuinely silent room at 0.0001 were the same mark — which is the exact distinction a
// wrong cut turns on.
const glyph = (
  rms: number,
  { deadRms, enterRms, stayRms }: { deadRms: number; enterRms: number; stayRms: number }
) => {
  if (rms >= enterRms) {
    return "█";
  }
  if (rms >= stayRms) {
    return "▓";
  }
  if (rms >= deadRms) {
    return "▒";
  }
  return rms >= deadRms / 10 ? "░" : "·";
};

export const debugLogCut = (
  reason: VoiceCutReason,
  { clipMs, emit, quietMs, rms }: { clipMs: number; emit: boolean; quietMs: number; rms: number }
): void => {
  if (!enabled) {
    return;
  }
  const scale = {
    deadRms: getVoiceTunable("DEAD_MIC_RMS"),
    enterRms: getVoiceTunable("SPEECH_ENTER_RMS"),
    stayRms: getVoiceTunable("SPEECH_STAY_RMS"),
  };
  const silenceMs = getVoiceTunable("SILENCE_MS");
  const buckets = recentBuckets();

  console.log(
    `[voice] cut · ${reason} · clip ${Math.round(clipMs)}ms · quiet ${Math.round(quietMs)}ms` +
      ` / ${silenceMs}ms · rms ${rms.toFixed(4)} · ${emit ? "SEND" : "DROP"}\n` +
      `        ${buckets.map((bucket) => glyph(bucket, scale)).join("")}` +
      `  ← ${BUCKET_MS}ms per mark, oldest left, cut at the right\n` +
      `        █ ≥ enter ${scale.enterRms}   ▓ ≥ stay ${scale.stayRms}` +
      `   ▒ ≥ ${scale.deadRms} (real signal, still counts as quiet)` +
      `   ░ trace   · silence`,
    { peaks: buckets.map((bucket) => Number(bucket.toFixed(4))) }
  );
};
